// components/Sidebar.js
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Divider,
  useMediaQuery,
  useTheme,
  Drawer,
  Tooltip,
} from "@mui/material";
import { useState, useEffect } from "react";
import DashboardIcon from "@mui/icons-material/Dashboard";
import DescriptionIcon from "@mui/icons-material/Description";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import AssessmentIcon from "@mui/icons-material/Assessment";
import FolderIcon from "@mui/icons-material/Folder";
import AssignmentIcon from "@mui/icons-material/Assignment";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import SettingsIcon from "@mui/icons-material/Settings";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import GroupsIcon from "@mui/icons-material/Groups";
import InsightsIcon from "@mui/icons-material/Insights";
import LocalAtmIcon from "@mui/icons-material/LocalAtm";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { useATS } from "../context/ATSContext";

const Sidebar = ({ mobileOpen, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { userRole } = useATS();

  const [assessmentsOpen, setAssessmentsOpen] = useState(true);
  const [sopOpen, setSopOpen] = useState(true);

  // ── Collapsed to a rail, expanded on hover ────────────────────────────────
  //
  // The candidate table needs every pixel it can get and this nav is read once
  // a session, so it gives the width back and takes it only while the pointer
  // is over it.
  //
  // The RAIL keeps its 64px in the layout at all times and the panel that grows
  // is absolutely positioned on top of the page. That distinction is the whole
  // trick: if the sidebar itself grew, the entire table would reflow — columns
  // jumping and rows re-wrapping — every time somebody's pointer crossed the
  // left edge on its way somewhere else. Overlaying costs a shadow and moves
  // nothing.
  const [hovered, setHovered] = useState(false);
  // Always open on mobile: it is a full drawer there, and a hover state on a
  // touch screen is a control nobody can reach.
  const expanded = isMobile || hovered;

  // Close mobile drawer when navigating
  const handleNav = (path) => {
    navigate(path);
    if (isMobile && onClose) onClose();
  };

  // Clicking a section header on the rail cannot toggle anything useful — the
  // children it would reveal have no room to be read — so while collapsed it
  // is the labels that are missing, not the behaviour: the sub-lists are held
  // shut and reopen with the panel.
  const toggleAssessments = () => expanded && setAssessmentsOpen(!assessmentsOpen);
  const toggleSop = () => expanded && setSopOpen(!sopOpen);

  // Role‑based items
  const adminAssessmentItems = [
    { label: "Assessment Folders", path: "/ats/assessments", icon: FolderIcon },
    {
      label: "Assessment Report",
      path: "/ats/assessments/report",
      icon: AssignmentIcon,
    },
  ];
  const staffAssessmentItems = [
    {
      label: "My Assessments",
      path: "/ats/my-assessments",
      icon: AssignmentIcon,
    },
  ];
  const assessmentItems =
    userRole === "admin"
      ? adminAssessmentItems
      : userRole === "staff"
      ? staffAssessmentItems
      : [...adminAssessmentItems, ...staffAssessmentItems];

  const sopItems = [
    { label: "SOP Folders", path: "/ats/sop", icon: LibraryBooksIcon },
  ];

  const mainItems = [
    { label: "Dashboard", path: "/ats", icon: DashboardIcon },
    // The chart is the main way in — it is where positions get read and edited.
    // `match` keeps it lit while a single department is in focus, which the
    // exact-match default cannot do.
    {
      label: "Org chart",
      path: "/ats/org",
      icon: AccountTreeIcon,
      match: (pathname) =>
        pathname === "/ats/org" || pathname.startsWith("/ats/departments/"),
    },
    // The plain list stays for creating and renaming departments themselves.
    { label: "Departments", path: "/ats/departments", icon: GroupsIcon },
    { label: "Templates", path: "/ats/templates", icon: DescriptionIcon },
    // Above AI usage rather than below it: this answers "what did the team
    // do", which gets asked far more often than "what did it cost", and the
    // two read as a pair with the activity one first.
    { label: "Activity log", path: "/ats/activity", icon: HistoryRoundedIcon },
    // Sits next to Settings rather than under it: the monthly cap is set in
    // Settings, but the question "what is this costing" gets asked far more
    // often than the cap gets changed, and burying a spend report inside a
    // configuration screen is how it goes unread.
    { label: "AI usage", path: "/ats/ai-usage", icon: InsightsIcon },
    // Kumudu only. Shown to everyone because the sidebar has no way to know
    // which tenant this is — the API answers 404 for a customer store, and the
    // page says so plainly rather than pretending to be broken.
    { label: "AI credits", path: "/ats/credits", icon: LocalAtmIcon },
    // The /ats/settings route has existed since the tenant settings page
    // landed, but nothing linked to it — and an unprovisioned tenant 403s out
    // of every other hiring route until it provisions itself here, so this is
    // the one screen that has to be reachable before anything else works.
    { label: "Settings", path: "/ats/settings", icon: SettingsIcon },
  ];

  const isActive = (path) => location.pathname === path;

  // An item may bring its own matcher when a child route should keep it lit.
  // Everything without one keeps the exact path match used everywhere else.
  const isItemActive = (item) =>
    item.match ? item.match(location.pathname) : isActive(item.path);

  // Width of the rail, and of the panel once it opens. Named because the two
  // have to agree in four places — the spacer, the panel, the mobile drawer and
  // the label fade — and a hardcoded 64 that drifts from a hardcoded 64 is the
  // kind of bug that only shows up as "the icons look slightly wrong".
  const RAIL = 64;
  const PANEL = 260;

  // A label that is present in the DOM at both widths and simply fades.
  //
  // Rendering it conditionally would reflow the row as it appears, so the icon
  // would shuffle sideways mid-transition. Fading a nowrap label that is
  // clipped by the panel's own overflow keeps every icon on the same x through
  // the whole animation.
  const Label = ({ text, size = 14, weight = 500 }) => (
    <ListItemText
      primary={text}
      sx={{
        my: 0,
        opacity: expanded ? 1 : 0,
        transition: "opacity .14s ease",
        whiteSpace: "nowrap",
      }}
      primaryTypographyProps={{ fontSize: size, fontWeight: weight, noWrap: true }}
    />
  );

  // On the rail the label is gone, so the icon has to say what it is somehow.
  // Suppressed once expanded, where the label is right there and a tooltip
  // repeating it is just something that flickers under the cursor.
  const Hint = ({ label, children }) =>
    expanded ? (
      children
    ) : (
      <Tooltip title={label} placement="right" arrow>
        {children}
      </Tooltip>
    );

  const rowSx = {
    borderRadius: 2,
    mb: 0.25,
    minHeight: 42,
    // Locked so the icon sits at the same x whether the panel is open or shut.
    // With the default padding the icons slide as the panel grows, which reads
    // as the whole nav wobbling.
    px: 1.5,
    justifyContent: "flex-start",
  };

  const iconSx = (active) => ({
    minWidth: 0,
    width: 34,
    flexShrink: 0,
    color: active ? "#0c8a9b" : "#64748b",
  });

  const content = (
    <Box
      onMouseEnter={() => !isMobile && setHovered(true)}
      onMouseLeave={() => !isMobile && setHovered(false)}
      sx={{
        width: isMobile ? PANEL : expanded ? PANEL : RAIL,
        height: "100%",
        bgcolor: "#fff",
        borderRight: "1px solid #e2e8f0",
        overflowX: "hidden",
        overflowY: "auto",
        transition: "width .18s cubic-bezier(.4,0,.2,1), box-shadow .18s",
        // Only while floating over the page. On the rail, or in the mobile
        // drawer, the border alone is the edge.
        boxShadow: !isMobile && expanded ? "8px 0 24px rgba(15,23,42,.10)" : "none",
        "&::-webkit-scrollbar": { width: 6 },
        "&::-webkit-scrollbar-thumb": { background: "#e2e8f0", borderRadius: 3 },
      }}
    >
      <Box sx={{ p: 1, pt: 1.5 }}>
        <Box
          sx={{
            mb: 1.5,
            px: 1,
            height: 30,
            display: "flex",
            alignItems: "center",
            // Left-aligned at both widths so the mark does not jump to the
            // middle as the panel opens.
            gap: 1,
            overflow: "hidden",
            whiteSpace: "nowrap",
          }}
        >
          <Box
            sx={{
              width: 28,
              height: 28,
              flexShrink: 0,
              borderRadius: 1.5,
              display: "grid",
              placeItems: "center",
              bgcolor: "#0c8a9b",
              color: "#fff",
              fontWeight: 800,
              fontSize: 12,
            }}
          >
            HB
          </Box>
          <Box
            component="span"
            sx={{
              fontWeight: 700,
              fontSize: 16,
              color: "#0c8a9b",
              opacity: expanded ? 1 : 0,
              transition: "opacity .14s ease",
            }}
          >
            Hiring Bible
          </Box>
        </Box>

        <List component="nav" disablePadding>
          {mainItems.map((item) => (
            <ListItem key={item.path} disablePadding>
              <Hint label={item.label}>
                <ListItemButton
                  selected={isItemActive(item)}
                  onClick={() => handleNav(item.path)}
                  sx={rowSx}
                >
                  <ListItemIcon sx={iconSx(isItemActive(item))}>
                    <item.icon fontSize="small" />
                  </ListItemIcon>
                  <Label text={item.label} />
                </ListItemButton>
              </Hint>
            </ListItem>
          ))}

          <Divider sx={{ my: 1 }} />

          {/* Assessments Section */}
          <ListItem disablePadding>
            <Hint label="Assessments">
              <ListItemButton onClick={toggleAssessments} sx={rowSx}>
                <ListItemIcon sx={iconSx(false)}>
                  <AssessmentIcon fontSize="small" />
                </ListItemIcon>
                <Label text="Assessments" />
                {expanded &&
                  (assessmentsOpen ? (
                    <ExpandLess sx={{ fontSize: 18, color: "#94a3b8" }} />
                  ) : (
                    <ExpandMore sx={{ fontSize: 18, color: "#94a3b8" }} />
                  ))}
              </ListItemButton>
            </Hint>
          </ListItem>
          <Collapse in={expanded && assessmentsOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {assessmentItems.map((item) => (
                <ListItemButton
                  key={item.path}
                  selected={isActive(item.path)}
                  onClick={() => handleNav(item.path)}
                  sx={{ ...rowSx, pl: 3.5, minHeight: 36 }}
                >
                  <ListItemIcon
                    sx={{
                      ...iconSx(isActive(item.path)),
                      width: 30,
                      color: isActive(item.path) ? "#0c8a9b" : "#94a3b8",
                    }}
                  >
                    <item.icon sx={{ fontSize: 18 }} />
                  </ListItemIcon>
                  <Label text={item.label} size={13} />
                </ListItemButton>
              ))}
            </List>
          </Collapse>

          {/* SOP Section */}
          <ListItem disablePadding>
            <Hint label="SOPs">
              <ListItemButton onClick={toggleSop} sx={rowSx}>
                <ListItemIcon sx={iconSx(false)}>
                  <MenuBookIcon fontSize="small" />
                </ListItemIcon>
                <Label text="SOPs" />
                {expanded &&
                  (sopOpen ? (
                    <ExpandLess sx={{ fontSize: 18, color: "#94a3b8" }} />
                  ) : (
                    <ExpandMore sx={{ fontSize: 18, color: "#94a3b8" }} />
                  ))}
              </ListItemButton>
            </Hint>
          </ListItem>
          <Collapse in={expanded && sopOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {sopItems.map((item) => (
                <ListItemButton
                  key={item.path}
                  selected={isActive(item.path)}
                  onClick={() => handleNav(item.path)}
                  sx={{ ...rowSx, pl: 3.5, minHeight: 36 }}
                >
                  <ListItemIcon
                    sx={{
                      ...iconSx(isActive(item.path)),
                      width: 30,
                      color: isActive(item.path) ? "#0c8a9b" : "#94a3b8",
                    }}
                  >
                    <item.icon sx={{ fontSize: 18 }} />
                  </ListItemIcon>
                  <Label text={item.label} size={13} />
                </ListItemButton>
              ))}
            </List>
          </Collapse>

          <Divider sx={{ my: 1 }} />

          {/* Switch to Daily Tick */}
          <ListItem disablePadding>
            <Hint label="Switch to Daily Tick">
              <ListItemButton onClick={() => handleNav("/home")} sx={rowSx}>
                <ListItemIcon sx={iconSx(false)}>
                  <SwapHorizIcon fontSize="small" />
                </ListItemIcon>
                <Label text="Switch to Daily Tick" />
              </ListItemButton>
            </Hint>
          </ListItem>
        </List>
      </Box>
    </Box>
  );

  // On desktop, render directly; on mobile, use a temporary drawer
  if (isMobile) {
    return (
      <Drawer
        anchor="left"
        open={mobileOpen}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        PaperProps={{ sx: { width: PANEL } }}
      >
        {content}
      </Drawer>
    );
  }
  return (
    // Two boxes on purpose. The outer one is a fixed-width spacer that holds the
    // rail's place in the flex row and never changes size; the inner panel is
    // taken out of flow and allowed to grow over the page. Collapsing these into
    // one would put the width change back into the layout and reflow the table
    // on every accidental hover.
    <Box sx={{ width: RAIL, flexShrink: 0, height: "100%", position: "relative" }}>
      <Box
        sx={{
          position: "absolute",
          insetBlock: 0,
          left: 0,
          zIndex: (t) => t.zIndex.drawer,
        }}
      >
        {content}
      </Box>
    </Box>
  );
};

export default Sidebar;
